import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { gate } from 'hub-http/gates';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { Details as DetailsPage } from 'marketplace-ui-details/Details';
import { useUnifiedDetails } from 'marketplace-ui-details/hooks/useUnifiedDetails';
import { UsageScreenProvider } from 'marketplace-ui-tracking';
import { useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { useParams, useRouteMatch } from 'react-router-dom';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { DetailsSuggestionSection } from '../components/DetailsSuggestionSection';
import { ListingDetailsStructuredData } from '../components/ListingDetailsStructuredData';
import { StorefrontPageMetadata } from '../components/StorefrontPageMetadata';
import { ProductPageHeader } from '../components/headers/ProductPageHeader';
import { UnifiedHeader } from '../components/headers/UnifiedHeader';
import { Paths } from '../constants/paths';
import { Screens } from '../constants/usage';
import { useAppContext } from '../context/AppContext';
import { useAuth, useIsUngated } from '../context/AuthContext';
import { useLocationHistory } from '../context/LocationHistoryContext';
import { useListingMetaDescription } from '../hooks/useListingMetaDescription';
import { useListingDocumentTitle } from '../hooks/useListingTitle';
import { ScrollToTopOnMount } from '../hooks/useScrollToTop';
import { getListingDetailsLoginlink } from '../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ListingDetailsContainer() {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    screen: Screens.LISTING_DETAILS,
    children: /*#__PURE__*/_jsx(ListingDetails, {})
  });
}
export function ListingDetails() {
  const {
    path
  } = useRouteMatch();
  const {
    listingSlug
  } = useParams();
  const appContext = useAppContext();
  const auth = useAuth();
  const isReviewLink = path.includes(Paths.LISTING_DETAILS_WRITE_REVIEW);
  const isUngatedForPartnerTierCard = useIsUngated(gate('Marketplace::TechPartnerTiers')) || appContext === 'public';
  function ListingDetailsNavMarker() {
    return isReviewLink ? /*#__PURE__*/_jsx(NavMarker, {
      name: "WRITE_REVIEW_LOAD"
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "LISTING_DETAILS_LOAD"
    });
  }
  function ListingDetailsErrorNavMarker() {
    return isReviewLink ? /*#__PURE__*/_jsx(NavMarker, {
      name: "WRITE_REVIEW_ERROR_LOAD"
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "LISTING_DETAILS_ERROR_LOAD"
    });
  }
  const {
    getBackLinkLocation
  } = useLocationHistory();
  const backLinkLocation = getBackLinkLocation();
  const {
    unifiedDetailsData,
    unifiedDetailsLoading,
    unifiedDetailsError
  } = useUnifiedDetails({
    appContext,
    slug: listingSlug,
    language: getLang()
  });
  const {
    unifiedDetailsData: unifiedDetailsDataV2,
    unifiedDetailsLoading: unifiedDetailsLoadingV2,
    unifiedDetailsError: unifiedDetailsErrorV2
  } = useUnifiedDetails({
    appContext,
    slug: listingSlug,
    language: getLang(),
    version: 2
  });
  const unifiedDetailsRequestStatus = getDataFetchingClientRequestState(unifiedDetailsError || unifiedDetailsErrorV2, unifiedDetailsLoading || unifiedDetailsLoadingV2);
  const formattedTitle = useListingDocumentTitle(unifiedDetailsData ? Object.assign({}, unifiedDetailsData, {
    name: unifiedDetailsData.name ? I18n.SafeString(unifiedDetailsData.name) : undefined,
    companyName: unifiedDetailsData.companyName ? I18n.SafeString(unifiedDetailsData.companyName) : undefined
  }) : undefined);
  const metaDescription = useListingMetaDescription({
    tagline: unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.tagline,
    certified: unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.builtByHubSpot,
    productType: unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.productType
  });
  const renderedHeaderComponent = useMemo(() => {
    if (unifiedDetailsRequestStatus === 'SUCCEEDED') {
      return /*#__PURE__*/_jsx(ProductPageHeader, {
        backBreadcrumb: !!backLinkLocation,
        pathKey: "LISTING_DETAILS",
        title: (unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.name) || '',
        productType: (unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.productType) || 'APP'
      });
    } else if (unifiedDetailsRequestStatus === 'FAILED') {
      return /*#__PURE__*/_jsx(UnifiedHeader, {
        backBreadcrumb: true
      });
    }
    return null;
  }, [unifiedDetailsRequestStatus, backLinkLocation, unifiedDetailsData]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StorefrontPageMetadata, {
      title: formattedTitle || undefined,
      metaDescription: metaDescription,
      robotsDirective: unifiedDetailsRequestStatus === 'FAILED' ? ['noindex', 'nofollow'] : ['follow'],
      loading: !isFulfilled(unifiedDetailsRequestStatus)
    }), /*#__PURE__*/_jsx(ScrollToTopOnMount, {}), unifiedDetailsRequestStatus === 'SUCCEEDED' && /*#__PURE__*/_jsx(ListingDetailsStructuredData, {
      unifiedDetailsData: Object.assign({}, unifiedDetailsData, {
        offeringId: unifiedDetailsDataV2 === null || unifiedDetailsDataV2 === void 0 ? void 0 : unifiedDetailsDataV2.offeringId
      })
    }), /*#__PURE__*/_jsxs(DashboardPage, {
      headerComponent: renderedHeaderComponent,
      mainSectionStyles: {
        paddingBlock: '1100'
      },
      children: [unifiedDetailsRequestStatus === 'SUCCEEDED' && /*#__PURE__*/_jsx(ListingDetailsNavMarker, {}), unifiedDetailsRequestStatus === 'FAILED' && /*#__PURE__*/_jsx(ListingDetailsErrorNavMarker, {}), /*#__PURE__*/_jsx(DetailsPage, {
        appContext: appContext,
        logInUrl: getListingDetailsLoginlink(`/listing/${listingSlug}`),
        renderedErrorLink: /*#__PURE__*/_jsx(RouterLink, {
          to: Paths.HOME,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplaceStorefront.listingDetails.backToMarketplace"
          })
        }),
        slug: listingSlug,
        userInfo: auth,
        isUngatedForPartnerTierCard: isUngatedForPartnerTierCard
      }), unifiedDetailsRequestStatus === 'SUCCEEDED' && /*#__PURE__*/_jsx(DetailsSuggestionSection, {
        productType: unifiedDetailsData === null || unifiedDetailsData === void 0 ? void 0 : unifiedDetailsData.productType
      })]
    })]
  });
}